import { useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import logoAlbaker from "@/assets/logo-albaker.png";
import { ArrowUpRight } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resetUrl, setResetUrl] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
      // In dev, the API returns the reset URL so the admin can copy/share it
      if (data?.reset_url) setResetUrl(data.reset_url);
      toast.success("إذا كان البريد مسجَّلاً، سيصل رابط إعادة التعيين قريبًا.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-ink text-bone flex items-center justify-center container-x py-20">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-3 mb-12">
          <img src={logoAlbaker} alt="Albaker" className="h-10 w-auto" />
          <span className="overline">العودة للموقع</span>
        </Link>

        <div className="overline mb-4">استعادة كلمة المرور</div>
        <h1 className="font-arabic font-black text-4xl sm:text-5xl text-bone leading-[1.15] mb-6">
          نسيت كلمة المرور؟
        </h1>
        <p className="text-white/55 font-arabic mb-10 leading-[1.9]">
          أدخل بريدك الإلكتروني وسنرسل لك رابطًا لإعادة تعيين كلمة المرور.
        </p>

        {done ? (
          <div className="space-y-6 font-arabic" data-testid="forgot-success">
            <div className="border border-gold/30 bg-gold/5 p-6 text-bone leading-[1.9]">
              إن كان البريد مسجَّلاً عندنا، فقد تم إنشاء رابط إعادة التعيين.
              تابع بريدك الإلكتروني — وإلّا تواصل مع المدير.
            </div>
            {resetUrl && (
              <div className="border border-white/10 bg-ink-soft p-5 text-white/65 text-sm" dir="ltr">
                <div className="overline mb-2 font-arabic">رابط مؤقت (وضع التطوير)</div>
                <a href={resetUrl} className="text-gold break-all hover:underline">{resetUrl}</a>
              </div>
            )}
            <Link to="/login" className="btn-ghost inline-flex">العودة لتسجيل الدخول</Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-7" data-testid="forgot-form">
            <label className="block">
              <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50">البريد الإلكتروني</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-input"
                data-testid="forgot-email"
                dir="ltr"
              />
            </label>
            <button type="submit" disabled={loading} className="btn-gold group disabled:opacity-60" data-testid="forgot-submit">
              <span>{loading ? "جاري الإرسال…" : "إرسال رابط الاستعادة"}</span>
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </form>
        )}

        <div className="mt-10 pt-8 border-t border-white/10 text-sm text-white/55 font-arabic">
          تذكرتها؟{" "}
          <Link to="/login" className="text-gold hover:text-gold-light transition-colors">
            عودة لتسجيل الدخول
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
